import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bulkCreateUsers,
  createUser,
  listUsers,
  resetOtp,
  setActive,
} from '@/api/users';

/**
 * Centralised query key so any mutation can invalidate the table with a
 * single line. Keep this stable — if you change it, change the
 * invalidate() calls below too.
 */
const USERS_QUERY_KEY = ['users'] as const;

export function useUsers() {
  return useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: listUsers,
    // 60s stale window — users don't churn second-by-second and a fresh
    // user shows up via cache invalidation on the create mutation anyway.
    staleTime: 60 * 1000,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}

export function useBulkCreateUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkCreateUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}

export function useResetOtp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => resetOtp(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}

export function useSetActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      setActive(userId, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}
